// TranslationModalContent.component.tsx
import React, { useState, useRef, useEffect, useCallback } from "react"
import { Select, Upload, Tooltip } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faArrowRightToBracket,
  faGripLinesVertical,
  faFileExport,
  faFileArrowDown,
  faCircleQuestion,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons"
import { useQuestionnaire } from "features/QuestionnaireEditor/api/useQuestionnaire"
import handleSequenceError from "utils/handleSequenceError"
import { HandledApiError } from "api/queryClient"
import { notify } from "shared/ui/Notifications/notify"
import { Loader } from "shared/ui/Loader/Loader.component"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { exportToExcel } from "utils/exportToExcel"
import { importExcelTranslations } from "utils/importExcelTranslations"
import { Project } from "stores/projectStore"
import { Variant } from "types/questionnaire.types"
import { Translation } from "types/questionnaire.types"
import TranslationRow from "./TranslationRow.component"

const { Option } = Select

/**
 * Interface for column widths
 */
interface ColumnWidths {
  master: number
  translation: number
}

/**
 * Interface for resizing data
 */
interface ResizingData {
  column: keyof ColumnWidths | null
  startX: number
  startWidth: number
}

/**
 * Props interface for the TranslationModalContent component
 */
interface TranslationModalContentProps {
  /** Function to save the questionnaire */
  saveQuestionnaire: (updatedVariants?: Variant[]) => Promise<void>
  /** The project object */
  project: Project
  /** Whether parsing failed */
  parseFailed: boolean
  /** Function to set modal visibility */
  setIsModalVisible: (visible: boolean) => void
}

/**
 * TranslationModalContent component for managing translation content
 *
 * @component
 * @param props - The component props
 * @returns The rendered TranslationModalContent component
 */
const TranslationModalContent: React.FC<TranslationModalContentProps> = ({
  saveQuestionnaire,
  project,
  parseFailed,
  setIsModalVisible,
}) => {
  const [translations, setTranslations] = useState<Translation[]>([])
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false)
  const [availableLanguages, setAvailableLanguages] = useState<string[]>([])
  const [selectedLanguage, setSelectedLanguage] = useState<string>("")
  const [
    isGettingQuestionnaireTranslations,
    setIsGettingQuestionnaireTranslations,
  ] = useState<boolean>(false)

  const [columnWidths, setColumnWidths] = useState<ColumnWidths>({
    master: 300,
    translation: 300,
  })

  const resizingData = useRef<ResizingData>({
    column: null,
    startX: 0,
    startWidth: 0,
  })

  const {
    getQuestionnaireTranslationsByID,
    saveQuestionnaireTranslationsByID,
  } = useQuestionnaire()

  /**
   * Fetches translations for the current project
   *
   * @async
   * @function getTranslations
   */
  const getTranslations = async (): Promise<void> => {
    setIsGettingQuestionnaireTranslations(true)
    try {
      await saveQuestionnaire()
      const res = await getQuestionnaireTranslationsByID(
        project.id,
        project.Created_by
      )

      if (handleSequenceError(res.data, "Error Fetching Translations!")) {
        if (!res.data.result) {
          setTranslations([])
          setAvailableLanguages([])
        } else {
          const translationData: Translation[] = res.data.result
          setTranslations(translationData)

          // Determine available languages
          const languagesSet = new Set<string>()
          translationData.forEach((item: Translation) => {
            Object.keys(item).forEach((key: string) => {
              if (
                key !== "identifier" &&
                key !== "MASTER" &&
                key !== project.Designlanguage &&
                !languagesSet.has(key)
              ) {
                languagesSet.add(key)
              }
            })
          })

          const languagesArray: string[] = Array.from(languagesSet)

          setAvailableLanguages(languagesArray)
          if (languagesArray.length > 0) {
            setSelectedLanguage(languagesArray[0])
          } else {
            setSelectedLanguage("")
          }
        }
      }
    } catch (error) {
      // A failed questionnaire save has already said so
      if (!(error instanceof HandledApiError)) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error occurred"
        notify.error("Error Fetching Translation!", errorMessage)
      }
    }
    setIsGettingQuestionnaireTranslations(false)
  }

  /**
   * React effect to fetch translations on mount
   */
  useEffect(() => {
    if (!parseFailed) {
      getTranslations()
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parseFailed])

  /**
   * Handles the initialization of the column resize
   *
   * @param e - The mouse event
   * @param column - The column being resized
   */
  const initResize = (
    e: React.MouseEvent,
    column: keyof ColumnWidths
  ): void => {
    e.preventDefault()
    resizingData.current = {
      column,
      startX: e.clientX,
      startWidth: columnWidths[column],
    }
    window.addEventListener("mousemove", handleResize)
    window.addEventListener("mouseup", stopResize)
  }

  /**
   * Handles the resizing of the column
   *
   * @param e - The mouse event
   */
  const handleResize = (e: MouseEvent): void => {
    e.preventDefault()
    if (!resizingData.current.column) return

    const deltaX = e.clientX - resizingData.current.startX
    const newWidth = resizingData.current.startWidth + deltaX

    setColumnWidths((prevWidths: ColumnWidths) => {
      const updatedWidths = { ...prevWidths }
      updatedWidths[resizingData.current.column!] =
        newWidth > 50 ? newWidth : 50 // Minimum width
      return updatedWidths
    })
  }

  /**
   * Stops the resizing operation
   */
  const stopResize = (): void => {
    resizingData.current = { column: null, startX: 0, startWidth: 0 }
    window.removeEventListener("mousemove", handleResize)
    window.removeEventListener("mouseup", stopResize)
  }

  /**
   * Handles changing the selected language
   *
   * @param value - The selected language
   */
  const handleLanguageChange = (value: string): void => {
    setSelectedLanguage(value)
  }

  /**
   * Handles changing a translation directly in the table
   *
   * @param identifier - The identifier of the translation
   * @param newValue - The new translation value
   */
  const handleTranslationChange = useCallback(
    (identifier: string, newValue: string): void => {
      setTranslations((prevTranslations: Translation[]) =>
        prevTranslations.map((item: Translation) =>
          item.identifier === identifier
            ? { ...item, [selectedLanguage]: newValue }
            : item
        )
      )
      setHasUnsavedChanges(true)
    },
    [selectedLanguage]
  )

  /**
   * Saves the current translations
   *
   * @async
   * @function handleSaveTranslations
   */
  const handleSaveTranslations = async (): Promise<void> => {
    try {
      await saveQuestionnaire()
      const res = await saveQuestionnaireTranslationsByID(
        project.id,
        project.Created_by,
        translations
      )
      if (handleSequenceError(res.data, "Error Saving Translations!")) {
        notify.success(
          "Translations Saved Successfully!",
          "Translations have been saved successfully!"
        )
      }
      setHasUnsavedChanges(false)
    } catch (error) {
      // A failed questionnaire save has already said so
      if (!(error instanceof HandledApiError)) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error occurred"
        notify.error("Error Saving Translation!", errorMessage)
      }
    }
  }

  /**
   * Handles the export of translations to an Excel file.
   *
   * This function checks if translations are available, and if so, it calls the exportToExcel
   * function to create and download an Excel file containing the translations. It also
   * handles success and error notifications.
   *
   * @function handleExportTranslations
   * @throws Will throw an error if the export process fails
   */
  const handleExportTranslations = async (): Promise<void> => {
    if (!translations || translations.length === 0) {
      notify.error("Export Failed", "No translations available to export.")
      return
    }

    try {
      const fileName = `translations_${project.Name}`
      await exportToExcel(translations, fileName, {
        designLanguage: project.Designlanguage,
      })
      notify.success(
        "Export Successful",
        `Translations exported to ${fileName}.xlsx`
      )
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred"
      notify.error("Export Failed", errorMessage)
    }
  }

  /**
   * Handles the import of translations from an Excel file.
   *
   * @param file - The uploaded file
   */
  const handleImportTranslations = async (file: File): Promise<void> => {
    if (!file) {
      notify.error("Import Failed", "No file selected.")
      return
    }
    try {
      const { translations: importedTranslations, languages } =
        await importExcelTranslations(file, translations)

      setTranslations(importedTranslations)

      // Update available languages
      setAvailableLanguages((prevLanguages: string[]) => {
        const newLanguages = new Set([...prevLanguages, ...languages])
        return Array.from(newLanguages)
      })

      notify.success(
        "Import Successful",
        "Translations have been imported successfully!"
      )
      setHasUnsavedChanges(true)
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred"
      notify.error("Import Failed", errorMessage)
    }
  }

  /**
   * Renders the main content of the modal
   *
   * @returns The rendered modal content
   */
  const renderModalContent = () => {
    if (parseFailed) {
      return renderParseFailedContent()
    }

    if (isGettingQuestionnaireTranslations) {
      return renderLoadingContent()
    }

    return renderTranslationsContent()
  }

  /**
   * Renders the content shown when parsing fails
   *
   * @returns The rendered parse failed content
   */
  const renderParseFailedContent = () => (
    <div className="shadow-sm overflow-hidden flex-grow flex flex-col">
      <p className="text-md font-semibold">
        Parsing of the questionnaire failed!
      </p>
      <p className="text-md">
        Make sure there are no errors before trying to access translations
      </p>
      <p className="text-md">
        Use the check questionnaire button{" "}
        <FontAwesomeIcon
          className="text-[var(--dq-brand)] mr-2"
          icon={faArrowRightToBracket}
        />
        to see the errors
      </p>
    </div>
  )

  /**
   * Renders the loading content
   *
   * @returns The rendered loading content
   */
  const renderLoadingContent = () => (
    <div className="flex flex-col items-center justify-center h-full">
      <Loader fontSize={48} />
      Loading Translations...
    </div>
  )

  /**
   * Renders the translations content
   *
   * @returns The rendered translations content
   */
  const renderTranslationsContent = () => {
    if (!translations || translations.length === 0) {
      return (
        <div>
          <p>
            No translations available. Please add content (questions/text etc.)
            and variants with different languages into the questionnaire to
            create translations.
          </p>
        </div>
      )
    }

    // Filter translations to include only those that have the selected language
    const filteredTranslations = translations.filter(
      (item: Translation) => selectedLanguage in item
    )

    return (
      <div className="overflow-auto">
        <div className="flex items-center mb-4 justify-between">
          <div className="flex items-center">
            <span className="mr-2">Select Language:</span>
            <Select
              value={selectedLanguage}
              onChange={handleLanguageChange}
              style={{ width: 200 }}
            >
              {availableLanguages.map((lang: string) => (
                <Option key={lang} value={lang}>
                  {lang}
                </Option>
              ))}
            </Select>
          </div>
          <div className="flex">
            <Tooltip title="Export translations to Excel. You can edit and re-import this file later using the import button.">
              <FontAwesomeIcon
                icon={faCircleQuestion}
                className="text-[var(--dq-brand)] h-4 mr-2 my-auto"
              />
            </Tooltip>
            <DataQButton
              onClick={handleExportTranslations}
              type="secondary"
              isDisabled={translations.length === 0}
            >
              Export
              <FontAwesomeIcon icon={faFileExport} />
            </DataQButton>
            <Tooltip title="Import only fills empty translation slots. To update existing translations, edit them directly in this dialog.">
              <Upload
                accept=".xlsx, .xls"
                showUploadList={false}
                beforeUpload={(file: File) => {
                  handleImportTranslations(file)
                  return false // Prevent automatic upload
                }}
              >
                <DataQButton type="secondary" className="mx-2">
                  Import
                  <FontAwesomeIcon icon={faFileArrowDown} />
                </DataQButton>
              </Upload>
            </Tooltip>
          </div>
        </div>
        {selectedLanguage ? (
          filteredTranslations.length > 0 ? (
            <table
              className="w-full border-collapse"
              style={{ tableLayout: "fixed" }}
            >
              <thead className="sticky top-0 bg-gray-100">
                <tr className="bg-gray-100">
                  <th
                    className="p-2 border relative"
                    style={{ width: columnWidths.master }}
                  >
                    <div className="flex items-center justify-between">
                      <span>Original</span>
                      <FontAwesomeIcon
                        icon={faGripLinesVertical}
                        className="resize-icon"
                        onMouseDown={(e: React.MouseEvent) =>
                          initResize(e, "master")
                        }
                      />
                    </div>
                  </th>
                  <th
                    className="p-2 border relative"
                    style={{ width: columnWidths.translation }}
                  >
                    <div className="flex items-center justify-between">
                      <span>{selectedLanguage}</span>
                      <FontAwesomeIcon
                        icon={faGripLinesVertical}
                        className="resize-icon"
                        onMouseDown={(e: React.MouseEvent) =>
                          initResize(e, "translation")
                        }
                      />
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredTranslations.map((item: Translation) => (
                  <TranslationRow
                    key={item.identifier}
                    item={item}
                    selectedLanguage={selectedLanguage}
                    masterWidth={columnWidths.master}
                    translationWidth={columnWidths.translation}
                    onTranslationChange={handleTranslationChange}
                  />
                ))}
              </tbody>
            </table>
          ) : (
            <div>No translations available for {selectedLanguage}</div>
          )
        ) : (
          <div>No languages available to translate.</div>
        )}
      </div>
    )
  }

  return (
    <div className="bg-white flex flex-col max-h-[calc(100vh-300px)]">
      <div className="flex-grow overflow-auto px-1">{renderModalContent()}</div>
      <div className="flex justify-end mt-4 space-x-2 bg-white pt-2 pb-4 px-4">
        {hasUnsavedChanges && (
          <div className="flex items-center text-yellow-600">
            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
            <span>You have unsaved changes</span>
          </div>
        )}
        <DataQButton
          type="primary"
          isDisabled={
            parseFailed ||
            isGettingQuestionnaireTranslations ||
            !translations ||
            translations.length === 0 ||
            !selectedLanguage
          }
          onClick={handleSaveTranslations}
        >
          Save Translations
        </DataQButton>
        <DataQButton onClick={() => setIsModalVisible(false)} type="secondary">
          Cancel
        </DataQButton>
      </div>
    </div>
  )
}

export default TranslationModalContent
